import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Lock, ArrowRight, ShieldCheck, Trophy, AlertTriangle, Sparkles } from 'lucide-react';

// A.P.E.X. course overview.
// Shows Modules 1-4 with per-lesson completion state, quiz gate,
// dossier download (locked until 100% quiz), and COPPA VPC banner
// for under-13 users.
export default function ApexCourse() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/apex/course');
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Failed to load APEX course');
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <p className="text-red-400 mb-4" data-testid="apex-course-error">{err}</p>
          <Button onClick={() => navigate('/academy/login?next=/apex/course')} data-testid="apex-course-login-btn">Sign in</Button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-black text-white p-8" data-testid="apex-course-loading">Loading A.P.E.X.…</div>;

  const { course, progress, coppa } = data;
  const needsVpc = coppa?.under_13 && !coppa?.verified;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="apex-course-page">
      {/* Hero */}
      <div className="border-b border-gray-800/60 bg-gradient-to-b from-[#111] to-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-orange-500 text-black">ELECTIVE</Badge>
            <Badge variant="outline" className="border-gray-700 text-gray-400">Scholar Athlete Academy</Badge>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-2" data-testid="apex-course-title">{course.title}</h1>
          <p className="text-lg text-gray-400 mb-2">{course.tagline}</p>
          {course.author && (
            <p className="text-xs uppercase tracking-widest text-orange-400 mb-6" data-testid="apex-course-author">
              Course authored by {course.author.name} · <span className="text-gray-500">{course.author.title}</span>
            </p>
          )}
          <div className="flex items-center gap-4 max-w-lg">
            <Progress value={progress.percent_complete} className="h-2 flex-1" data-testid="apex-progress-bar" />
            <span className="text-sm text-gray-400 font-mono" data-testid="apex-progress-label">{progress.percent_complete}%</span>
          </div>
        </div>
      </div>

      {/* COPPA VPC banner */}
      {needsVpc && (
        <div className="max-w-5xl mx-auto px-6 pt-6">
          <div className="border border-amber-500/40 bg-amber-500/5 rounded-lg p-4 flex items-start gap-3" data-testid="apex-coppa-banner">
            <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-amber-300 mb-1">Parental Consent Required</div>
              <p className="text-sm text-gray-300 mb-3">
                This student is under 13. Under COPPA, a parent must verify consent before public-profile features or community posting are unlocked.
                Verification uses a one-time $0.01 charge that is refunded within 24 hours.
              </p>
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                onClick={() => navigate('/apex/coppa/verify')} data-testid="apex-coppa-verify-btn">
                Start Parent Verification
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modules */}
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {course.modules.map((m) => (
          <Card key={m.id} className="bg-[#111] border border-gray-800/60" data-testid={`apex-module-${m.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4 gap-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Module {m.id}</div>
                  <h2 className="text-xl font-bold mb-1">{m.title}</h2>
                  <p className="text-sm text-gray-400">{m.subtitle}</p>
                </div>
                {m.status === 'coming_soon' && (
                  <Badge variant="outline" className="border-gray-700 text-gray-500 whitespace-nowrap">Coming Soon</Badge>
                )}
              </div>

              {/* Module-level legal banner (used by Module 3) */}
              {m.legal_banner && (
                <div className="mb-4 p-3 rounded border border-amber-500/40 bg-amber-500/5 flex items-start gap-2"
                     data-testid={`apex-module-${m.id}-legal-banner`}>
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-200/90 leading-relaxed">{m.legal_banner}</p>
                </div>
              )}

              {m.lessons.length === 0 ? (
                <div className="text-sm text-gray-500 border border-gray-800/60 rounded p-3">
                  Module content unlocks in Phase 2. Complete Modules 1 &amp; 2 and pass the Mastery Quiz to earn early access.
                </div>
              ) : (
                <div className="space-y-2">
                  {m.lessons.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => navigate(`/apex/course/lesson/${l.id}`)}
                      className="w-full flex items-center justify-between p-3 rounded border border-gray-800/60 hover:border-orange-500/50 hover:bg-orange-500/5 transition-colors text-left"
                      data-testid={`apex-lesson-link-${l.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {l.completed
                          ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                          : <div className="w-5 h-5 rounded-full border-2 border-gray-700" />}
                        <div>
                          <div className="font-semibold">{l.title}</div>
                          <div className="text-xs text-gray-500">~{l.estimated_minutes} min</div>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-500" />
                    </button>
                  ))}
                </div>
              )}

              {/* AI tool row — Phase 2 tools are unlocked and clickable */}
              {m.ai_tool && (
                m.ai_tool.status === 'unlocked' && m.ai_tool.route ? (
                  <button
                    onClick={() => navigate(m.ai_tool.route)}
                    className="mt-4 w-full flex items-center justify-between p-3 rounded bg-orange-500/10 border border-orange-500/40 hover:bg-orange-500/20 transition-colors"
                    data-testid={`apex-module-${m.id}-tool-btn`}
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-orange-400" />
                      <span className="text-sm text-orange-200"><span className="font-semibold">AI Tool:</span> {m.ai_tool.title}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-orange-400" />
                  </button>
                ) : (
                  <div className="mt-4 flex items-center justify-between p-3 rounded bg-black/40 border border-orange-500/20"
                       data-testid={`apex-module-${m.id}-tool-comingsoon`}>
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-orange-400/70" />
                      <span className="text-sm text-gray-300"><span className="text-orange-500 font-semibold">AI Tool:</span> {m.ai_tool.title}</span>
                    </div>
                    <span className="text-xs text-orange-300/80 uppercase tracking-widest whitespace-nowrap">Coming Soon · Unlocks with APEX Purchase</span>
                  </div>
                )
              )}
            </CardContent>
          </Card>
        ))}

        {/* Mastery Quiz Gate */}
        <Card className={`border ${progress.mastery_passed ? 'border-green-500/40 bg-green-500/5' : 'border-orange-500/40 bg-orange-500/5'}`}
              data-testid="apex-quiz-gate-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <Trophy className={`w-6 h-6 ${progress.mastery_passed ? 'text-green-400' : 'text-orange-400'}`} />
              <div className="flex-1">
                <h3 className="text-lg font-bold mb-1">Mastery Quiz — 100% Required</h3>
                <p className="text-sm text-gray-400 mb-3">
                  10 scenario-based questions covering Modules 1 &amp; 2. Pass at 100% to unlock the SAA Verified Marketable Athlete badge and the Recruiting Dossier PDF export.
                </p>
                {progress.quiz_score != null && (
                  <div className="text-sm text-gray-300 mb-3">
                    Your best score: <span className="font-bold">{progress.quiz_score}%</span>
                    {progress.mastery_passed && <span className="ml-2 text-green-400">· PASSED ✓</span>}
                  </div>
                )}
                <Button
                  onClick={() => navigate('/apex/course/quiz')}
                  className={progress.mastery_passed ? 'bg-green-500 hover:bg-green-600 text-black' : 'bg-orange-500 hover:bg-orange-600 text-black'}
                  data-testid="apex-quiz-start-btn"
                >
                  {progress.mastery_passed ? 'Retake Quiz' : 'Start Mastery Quiz'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer footer */}
        <div className="text-[10px] text-gray-600 border-t border-gray-800/40 pt-4 flex items-start gap-2" data-testid="apex-course-disclaimer">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>
            EDUCATIONAL GUIDANCE ONLY — NOT LEGAL ADVICE. High school association bylaws (CIF, FHSAA, UIL) and state laws shift frequently. Always verify agreements with a licensed attorney or your high school athletic director before signing.
          </span>
        </div>
      </div>
    </div>
  );
}
